'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

// Value-anchored deep pagination — design search.jsx (grid pager). Bare page
// numbers give no reason to jump deep into 250+ pages; the sort key's value at
// 0/25/50/75/100% does ("27 000 Kč · p. 66"). The parent samples those five
// offsets once; this renders the chips for whichever view is showing, mapping
// each absolute offset onto that view's page size (list limit vs map batch).

export type JumpKind = 'price' | 'date' | 'area' | 'rooms'
export interface Waypoint {
  /** Absolute offset into the sorted result set (view-agnostic). */
  offset: number
  label: string
}

const JUMP_LABEL: Record<JumpKind, [string, string]> = {
  price: ['search.jumpByPrice', 'Jump by price'],
  date: ['search.jumpByDate', 'Jump by date'],
  area: ['search.jumpByArea', 'Jump by area'],
  rooms: ['search.jumpByRooms', 'Jump by layout'],
}

export interface JumpWaypointsProps {
  jumpKind: JumpKind
  waypoints: Waypoint[] | null
  loading: boolean
  onLoad: () => void
  /** Listings per page for this view (list limit, or the map's batch of 100). */
  pageSize: number
  currentPage: number
  onJump: (page: number) => void
  disabled?: boolean
  className?: string
}

export function JumpWaypoints({
  jumpKind,
  waypoints,
  loading,
  onLoad,
  pageSize,
  currentPage,
  onJump,
  disabled,
  className,
}: JumpWaypointsProps) {
  const { t } = useI18n()
  const label = t(...JUMP_LABEL[jumpKind])

  // Map each sampled offset onto this view's page size, dropping collisions
  // (two waypoints can land on one page when the page size is large).
  const chips = React.useMemo(() => {
    if (!waypoints) return null
    const seen = new Set<number>()
    const out: Array<{ page: number; label: string }> = []
    for (const w of waypoints) {
      const page = Math.floor(w.offset / pageSize) + 1
      if (seen.has(page)) continue
      seen.add(page)
      out.push({ page, label: w.label })
    }
    return out
  }, [waypoints, pageSize])

  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-1.5', className)}>
      {chips ? (
        <>
          <span className="mr-1 text-xs text-ink-muted">{label}:</span>
          {chips.map((c) => (
            <button
              key={c.page}
              type="button"
              onClick={() => onJump(c.page)}
              disabled={disabled || c.page === currentPage}
              title={`${t('search.page', 'Page')} ${c.page}`}
              className={cn(
                'inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1 text-[12px] transition-colors disabled:cursor-not-allowed',
                c.page === currentPage
                  ? 'border-primary bg-primary-container'
                  : 'border-outline bg-surface hover:bg-surface-high'
              )}
            >
              <span className="font-mono font-bold">{c.label}</span>
              <span className="text-[11px] text-ink-muted">
                {t('search.pageAbbr', 'p.')} {c.page}
              </span>
            </button>
          ))}
        </>
      ) : (
        <button
          type="button"
          onClick={onLoad}
          disabled={loading}
          className="rounded-full px-3 py-1 text-xs font-semibold text-ink-muted transition-colors hover:bg-surface-high hover:text-foreground disabled:opacity-50"
        >
          {loading ? t('notifications.loading', 'Loading...') : `${label}…`}
        </button>
      )}
    </div>
  )
}
